import { useCallback, type ChangeEvent } from "react";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { PubmedQueryText } from "../../../../../components/domain/data-display/PubmedQueryText";
import { PubmedQueryInput } from "../../../../../components/domain/inputs/PubmedQueryInput";
import { Note } from "../../../../../components/primitives/data-display/Note";
import type { UsePubmedIngestParamsResult } from "./usePubmedIngestParams";

export interface PubmedIngestParamsFormProps {
  form: UsePubmedIngestParamsResult;
}

/** The params a pubmed_ingest job asks for: a PubMed Central query and a limit. The query is
 *  validated against PMC before the job can start — its match count appears here for the person to
 *  confirm, and editing the query asks for the check again. */
export function PubmedIngestParamsForm({ form }: PubmedIngestParamsFormProps) {
  const { setQuery, setMaxResultsText } = form;
  const { preview, syntaxProblem, validationError } = form;

  const handleMaxResultsChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => setMaxResultsText(e.target.value),
    [setMaxResultsText]
  );

  return (
    <>
      {!form.extractionWarm && (
        <Alert severity="info">
          Kodakku is still starting up — this should only take a few seconds. Validating a query is
          disabled until it&apos;s ready, and the job can&apos;t be created before it is validated.
        </Alert>
      )}
      <PubmedQueryInput
        label="PubMed Central query"
        value={form.query}
        onChange={setQuery}
        minRows={3}
        error={syntaxProblem !== null}
        helperText={
          syntaxProblem ??
          'As typed into the PMC search box, e.g. semaglutide AND "randomized controlled trial"[pt]'
        }
      />
      <TextField
        label="Maximum articles"
        type="number"
        value={form.maxResultsText}
        onChange={handleMaxResultsChange}
        size="small"
        error={form.maxResultsProblem !== null}
        helperText={
          form.maxResultsProblem ?? "Each article is a full extraction, so this is the job's cost."
        }
        slotProps={{ htmlInput: { min: 1, step: 1 } }}
      />

      <Stack direction="row" gap={1} sx={{ alignItems: "center" }}>
        <Button
          variant="outlined"
          size="small"
          onClick={form.validate}
          disabled={!form.canValidate}
        >
          {form.validating ? "Validating…" : "Validate query"}
        </Button>
        {!preview && !form.validating && !validationError && (
          <Typography variant="caption" color="text.secondary">
            Validate the query to see how many articles it matches.
          </Typography>
        )}
      </Stack>

      {validationError && <Alert severity="error">{validationError}</Alert>}

      {preview && (
        <Stack gap={1}>
          <Alert severity={preview.count > 0 ? "success" : "warning"}>
            {preview.count > 0
              ? `${preview.count.toLocaleString()} articles match. This job will ingest ${form.articlesToIngest?.toLocaleString() ?? "up to the limit of"} of them.`
              : "No article matches this query."}
          </Alert>
          {preview.warnings.length > 0 && (
            <Alert severity="warning">
              <Stack component="ul" gap={0.25} sx={{ m: 0, pl: 2 }}>
                {preview.warnings.map((warning) => (
                  <li key={warning}>{warning}</li>
                ))}
              </Stack>
            </Alert>
          )}
          <Typography variant="caption" color="text.secondary">
            PMC read the query as:
          </Typography>
          <Typography variant="caption" component="div">
            <PubmedQueryText query={preview.queryTranslation} />
          </Typography>
        </Stack>
      )}

      <Note>
        Each article goes through the normal pipeline: the admission gate, annotation, and automatic
        ingestion. Anything that needs a person lands in the annotation queue.
      </Note>
    </>
  );
}
